import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Payment } from '@vsolv/packages/payments/domain';
import { PaymentService } from '@vsolv/packages/payments/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { CurrencyMaskConfig } from '@vsolv/vectors-ui/input';
import { Claim, Warranty } from '@wsphere/warranties/domain';
import moment from 'moment';
import { BehaviorSubject, combineLatest, filter, map, shareReplay, startWith, switchMap, tap } from 'rxjs';
import { ClaimService } from '../../../claim';
import { PolicyService } from '../../../policy';
import { WarrantyWebService } from '../../../warranty/services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-cancel-warranty-dialog',
  templateUrl: './cancel-warranty.dialog.html',
  standalone: false,
})
export class CancelWarrantyDialog {
  today = new Date();

  constructor(
    private toastSvc: ToastService,
    private claimSvc: ClaimService,
    private paymentSvc: PaymentService,
    private policySvc: PolicyService,
    private warrantySvc: WarrantyWebService
  ) {}

  protected currencyMaskConfig: CurrencyMaskConfig = {
    align: 'left',
    allowNegative: false,
    allowZero: false,
    decimal: '.',
    nullable: true,
    precision: 2,
    prefix: '$',
    suffix: '',
    thousands: ',',
    inputMode: 1,
    min: 0,
    max: 0,
  };

  @ViewChild(DialogComponent) private dialog?: DialogComponent;

  @ViewChild('cancelWarrantyDialog') cancelWarrantyDialog?: DialogComponent;
  @Output() readonly cancelled = new EventEmitter<Warranty.CancelWarrantyResponse>();
  /** Fired when the cancel attempt fails (e.g. the gateway rejects a refund) — lets the parent
   * page refresh the ledger without wrongly marking the warranty CANCELLED the way `cancelled` would. */
  @Output() readonly cancelFailed = new EventEmitter<void>();

  @Input() warranty?: Warranty.Model;
  @PropertyListener('warranty') private warranty$ = new BehaviorSubject<Warranty.Model | undefined>(undefined);

  cancellationReason = new FormControl('');
  protected refundControl = new FormControl({ value: null as number | null, disabled: false }, [Validators.min(0)]);

  protected saving = false;
  protected effectiveDate = new Date();
  @PropertyListener('effectiveDate') private effectiveDate$ = new BehaviorSubject<Date>(this.effectiveDate);

  protected cancelationRule$ = this.warranty$.pipe(
    filter(w => !!w),
    switchMap(async warranty => {
      if (!warranty) return null;
      const policy = await this.policySvc.getOne(warranty?.policyId);
      return policy?.cancelationRules?.find(
        rule => rule.country === warranty.shippingAddress?.country && rule.state === warranty.shippingAddress?.state
      );
    }),
    startWith(null),
    shareReplay(1)
  );

  protected payments$ = this.warranty$.pipe(
    switchMap(async warranty => {
      return warranty ? await this.paymentSvc.list({ referenceId: warranty.contractNumber, limit: 100 }) : null;
    }),
    map(data => data?.items ?? []),
    shareReplay(1)
  );

  protected totalPaidToDate$ = this.payments$.pipe(
    map(payments => {
      return {
        installments: payments.filter(payment => payment.status === Payment.Status.PAID).length,
        amount: payments.reduce(
          (acc, payment) =>
            acc +
            ([Payment.Status.PAID, Payment.Status.REFUNDED, Payment.Status.PARTIALLY_REFUNDED].includes(payment.status)
              ? payment.amount
              : 0),
          0
        ),
      };
    }),

    tap(({ amount }) => {
      this.currencyMaskConfig.max = amount / 100;
      this.refundControl.setValidators([Validators.min(0), Validators.max(amount / 100)]);
      this.refundControl.updateValueAndValidity();
    })
  );

  protected totalTaxPaid$ = this.payments$.pipe(
    map(payments => {
      const totalAmount = payments.reduce(
        (acc, payment) => acc + (payment.status === Payment.Status.PAID ? payment.amount : 0),
        0
      );

      const totalTaxAmount = payments.reduce(
        (acc, payment) => acc + (payment.status === Payment.Status.PAID ? payment.taxAmount : 0),
        0
      );

      return {
        percentage: Math.round((totalTaxAmount / (totalAmount - totalTaxAmount || 1)) * 100),
        amount: totalTaxAmount,
      };
    })
  );

  protected claims$ = this.warranty$.pipe(
    switchMap(async warranty => (warranty ? await this.claimSvc.list({ warrantyId: warranty.id, limit: 100 }) : null)),
    map(data => data?.items ?? []),
    shareReplay(1)
  );

  protected openClaims$ = this.claims$.pipe(map(claims => claims.filter(claim => claim.status === Claim.Status.OPEN)));

  protected totalClaimed$ = this.claims$.pipe(
    map(claims => {
      return {
        claims: claims.length,
        amount: claims.reduce((acc, claim) => acc + (claim.amount?.total ?? 0), 0),
      };
    })
  );

  protected durationStats$ = combineLatest([this.warranty$, this.effectiveDate$]).pipe(
    switchMap(async ([warranty, effectiveDate]) => {
      if (!warranty) return null;
      const stats = await this.warrantySvc.getDurationStats(warranty.id);
      stats.lastDay = effectiveDate;
      stats.numberOfDaysCompleted = stats.numberOfDaysCompleted - moment().diff(effectiveDate, 'days');
      stats.percentage = Math.round((stats.numberOfDaysCompleted / stats.termDuration) * 100);
      return stats;
    })
  );

  openCancelDialog() {
    this.cancelWarrantyDialog?.open();
  }

  protected async cancel(warranty: Warranty.Model, reason: string) {
    this.saving = true;

    try {
      const warrantyId = warranty?.id;
      if (!warrantyId) throw new Error('Unable to cancel warranty.');

      this.refundControl.disable();
      const refundAmount = this.refundControl.getRawValue() ?? undefined;

      const result = await this.warrantySvc.cancelWarranty(
        warrantyId,
        this.effectiveDate,
        reason,
        refundAmount ? Math.floor(refundAmount * 100) : undefined
      );
      this.cancelled.emit(result);

      this.toastSvc.show({
        type: 'success',
        title: 'Cancelled Warranty',
        text: `Warranty has successfully been cancelled ${
          refundAmount ? `and a $${refundAmount.toFixed(2)} refund issued to the customer` : ''
        }.`,
      });
      this.cancelWarrantyDialog?.close();
    } catch (err) {
      console.error(err);
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to cancel warranty', // TODO: localize
        text:
          err instanceof Error
            ? err.message
            : (err as { error: { message?: string } }).error.message
              ? (err as { error: { message?: string } }).error.message
              : 'Unknown Error',
      });
      this.close();
      // A failed attempt (e.g. a rejected refund) can still write ledger data (a $0 disbursement
      // documenting the attempt), so the parent needs to refresh even though cancellation didn't succeed.
      this.cancelFailed.emit();
    } finally {
      this.saving = false;
      this.refundControl.enable();
    }
  }

  open() {
    this.reset();
    this.dialog?.open();
  }

  close() {
    this.dialog?.close();
  }

  private reset() {
    this.refundControl.reset({ value: null, disabled: false });
  }
}
